'use client'

import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { toast } from 'sonner'
import { nanoid } from 'nanoid'
import { useEditor, EditorContent } from '@tiptap/react'
import TiptapStarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import TextStyle from '@tiptap/extension-text-style'
import Color from '@tiptap/extension-color'
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Link as LinkIcon,
  Palette,
} from 'lucide-react'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { FileUpload } from '@/components/ui/file-upload'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Plus, Trash2, X } from 'lucide-react'
import { popModal } from '@/components/modals'
import { IconSearch } from '@/components/ui/icon-search'
import type { StarterKit } from '@/lib/types'
import { useDynamicI18n } from '@/hooks/use-dynamic-i18n'

const ALLOWED_UPLOAD_ASSETS_FORMATS = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/svg+xml',
]

// Tiptap Rich Text Editor Component
const RichTextEditor = ({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}) => {
  const editor = useEditor({
    extensions: [
      TiptapStarterKit,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-500 underline',
        },
      }),
      TextStyle,
      Color,
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML())
    },
  })

  if (!editor) {
    return null
  }

  const addLink = () => {
    const url = window.prompt('Enter URL:')
    if (url) {
      editor.chain().focus().setLink({ href: url }).run()
    }
  }

  return (
    <div className="border rounded-md bg-gray-900">
      {/* Toolbar */}
      <div className="border-b border-gray-700 p-2 flex gap-1 flex-wrap bg-gray-800">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-2 rounded hover:bg-gray-600 text-white ${editor.isActive('bold') ? 'bg-gray-600' : ''}`}
          title="Bold"
        >
          <Bold className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-2 rounded hover:bg-gray-600 text-white ${editor.isActive('italic') ? 'bg-gray-600' : ''}`}
          title="Italic"
        >
          <Italic className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`p-2 rounded hover:bg-gray-600 text-white ${editor.isActive('bulletList') ? 'bg-gray-600' : ''}`}
          title="Bullet List"
        >
          <List className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`p-2 rounded hover:bg-gray-600 text-white ${editor.isActive('orderedList') ? 'bg-gray-600' : ''}`}
          title="Numbered List"
        >
          <ListOrdered className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={addLink}
          className={`p-2 rounded hover:bg-gray-600 text-white ${editor.isActive('link') ? 'bg-gray-600' : ''}`}
          title="Add Link"
        >
          <LinkIcon className="w-4 h-4" />
        </button>
      </div>

      {/* Editor Content */}
      <EditorContent
        editor={editor}
        className="prose prose-invert max-w-none p-4 min-h-[300px] focus:outline-none text-white [&_.ProseMirror]:text-white [&_.ProseMirror]:min-h-[250px] [&_.ProseMirror]:outline-none"
      />
    </div>
  )
}

const starterKitSchema = z
  .object({
    name: z.string().min(1, 'Name is required'),
    slug: z
      .string()
      .min(1, 'Slug is required')
      .regex(
        /^[a-z0-9-]+$/,
        'Slug must contain only lowercase letters, numbers, and hyphens',
      ),
    mode: z.enum(['template', 'scratch'], {
      required_error: 'Mode is required',
    }),
    base_template_id: z.string().optional(),
    template_category: z.string().min(1, 'Template category is required'),
    questions_rich_text: z.string().min(1, 'Questions content is required'),
    prompt_placeholder: z.string().optional(),
    system_prompt: z.string().min(1, 'System prompt is required'),
    user_message: z.string().min(1, 'User message is required'),
    superprompt_id: z.string().optional(),
    meta_title: z.string().optional(),
    meta_desc: z.string().optional(),
    x_title: z.string().optional(),
    x_desc: z.string().optional(),
    x_img: z.string().optional(),
    homepage_image: z.string().min(1, 'Homepage icon image is required'),
  })
  .refine(
    (data) => {
      // If mode is template, base_template_id is required
      if (data.mode === 'template' && !data.base_template_id) {
        return false
      }
      return true
    },
    {
      message: 'Base template is required when mode is template',
      path: ['base_template_id'],
    },
  )

type FormData = z.infer<typeof starterKitSchema>

interface Props {
  templateUseCaseData?: StarterKit
  onSuccess: () => void
  onClose?: () => void
}

export default function EditStarterKitModal({
  templateUseCaseData,
  onSuccess,
  onClose = popModal,
}: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { dt } = useDynamicI18n()

  const form = useForm<FormData>({
    resolver: zodResolver(starterKitSchema),
    defaultValues: templateUseCaseData || {
      name: '',
      slug: '',
      mode: 'template' as const,
      base_template_id: '',
      template_category: '',
      questions_rich_text: '',
      prompt_placeholder: '',
      system_prompt: '',
      user_message: '',
      // Superprompt integration
      superprompt_id: '',
      // SEO and social media social fields
      meta_title: '',
      meta_desc: '',
      x_title: '',
      x_desc: '',
      x_img: '',
      homepage_image: '',
    },
  })

  // Function to generate slug from name
  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .trim()
      .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens
  }

  // Watch name field and auto-generate slug
  const watchedName = form.watch('name')

  // Auto-update slug when name changes
  React.useEffect(() => {
    if (watchedName) {
      const newSlug = generateSlug(watchedName)
      form.setValue('slug', newSlug, { shouldValidate: true })
    }
  }, [watchedName, form])

  const onSubmit = async (data: FormData) => {
    try {
      setIsSubmitting(true)
      const res = await fetch('/api/dashboard/starter-kits', {
        method: templateUseCaseData ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-request-id': nanoid(12),
        },
        body: JSON.stringify(
          templateUseCaseData ? { ...data, id: templateUseCaseData._id } : data,
        ),
      })

      const result = await res.json()

      if (result.success) {
        toast.success(
          templateUseCaseData ? 'Updated successfully' : 'Created successfully',
        )
        onSuccess()
        onClose()
      } else {
        toast.error(result.error || 'Operation failed')
      }
    } catch (error) {
      toast.error('Operation failed')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>
              {templateUseCaseData ? dt('Edit') : dt('Create')}{' '}
              {dt('Starter Kit')}
            </DialogTitle>
          </div>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="basic">{dt('Basic Info')}</TabsTrigger>
                <TabsTrigger value="questions">{dt('Questions')}</TabsTrigger>
                <TabsTrigger value="seo">{dt('SEO/Social')}</TabsTrigger>
              </TabsList>

              <TabsContent value="basic" className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem className="space-y-2">
                        <FormLabel>{dt('Name')} *</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder={dt('e.g., Car Detailing')}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="slug"
                    render={({ field }) => (
                      <FormItem className="space-y-2">
                        <FormLabel>{dt('Slug')} *</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder={dt('Auto-generated from name')}
                            disabled
                            className="bg-muted/50 cursor-not-allowed text-muted-foreground"
                          />
                        </FormControl>
                        <FormDescription className="text-xs">
                          {dt(
                            'Auto-generated from name (URL-friendly identifier)',
                          )}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="mode"
                    render={({ field }) => (
                      <FormItem className="space-y-2">
                        <FormLabel>{dt('Mode')} *</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder={dt('Select mode')} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="template">
                              {dt('Template')}
                            </SelectItem>
                            <SelectItem value="scratch">
                              {dt('Scratch')}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <FormDescription className="text-xs">
                          {dt(
                            'Template mode uses an existing template, Scratch mode creates from nothing',
                          )}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="template_category"
                    render={({ field }) => (
                      <FormItem className="space-y-2">
                        <FormLabel>{dt('Template Category')} *</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder={dt('e.g., Website, App, Landing Page')}
                          />
                        </FormControl>
                        <FormDescription className="text-xs">
                          {dt('Category for organizing starter kits')}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {form.watch('mode') === 'template' && (
                  <FormField
                    control={form.control}
                    name="base_template_id"
                    render={({ field }) => (
                      <FormItem className="space-y-2">
                        <FormLabel>{dt('Base Template ID')} *</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder={dt(
                              'Enter template ID (required for template mode)',
                            )}
                          />
                        </FormControl>
                        <FormDescription className="text-xs">
                          {dt(
                            'The ID of the base template to use when mode is "template"',
                          )}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                <FormField
                  control={form.control}
                  name="superprompt_id"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel>{dt('Superprompt ID')}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={dt(
                            'Optional superprompt ID to auto-load',
                          )}
                        />
                      </FormControl>
                      <FormDescription className="text-xs">
                        {dt(
                          'Optional: Enter a superprompt ID to automatically load when this starter kit is used.',
                        )}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="user_message"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel>{dt('User Message')} *</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder={dt(
                            'The initial message from the user that will appear in the chat...',
                          )}
                          rows={3}
                        />
                      </FormControl>
                      <FormDescription className="text-xs">
                        {dt(
                          "This message will appear as the user's first message in the chat interface.",
                        )}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="prompt_placeholder"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel>{dt('Prompt Placeholder')}</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder={dt(
                            'Tell me about your requirements and what you want to create...',
                          )}
                          rows={2}
                        />
                      </FormControl>
                      <FormDescription className="text-xs">
                        {dt(
                          'Placeholder text that appears in the user input field.',
                        )}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="system_prompt"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel>{dt('System Prompt')} *</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder={dt(
                            'System instructions for AI responses...',
                          )}
                          rows={3}
                        />
                      </FormControl>
                      <FormDescription className="text-xs">
                        {dt(
                          'Instructions for how the AI should respond and behave for this starter kit.',
                        )}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="superprompt_id"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel>{dt('Superprompt ID')}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={dt(
                            'Enter superprompt ID to auto-fill superprompt (optional)',
                          )}
                        />
                      </FormControl>
                      <FormDescription className="text-xs">
                        {dt(
                          'Optional: Enter a superprompt ID to automatically include that superprompt in the starter kit. Users will see it pre-selected when using this starter kit.',
                        )}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </TabsContent>

              <TabsContent value="questions" className="space-y-6">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium">
                      {dt('Questions Rich Text')}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-2">
                      {dt(
                        "Questions and content that the AI will display to understand the user's business better. Use **bold text** for question headers and format as needed.",
                      )}
                    </p>
                  </div>

                  <FormField
                    control={form.control}
                    name="questions_rich_text"
                    render={({ field }) => (
                      <FormItem className="space-y-2">
                        <FormLabel>{dt('Questions Content')} *</FormLabel>
                        <FormControl>
                          <RichTextEditor
                            value={field.value || ''}
                            onChange={field.onChange}
                          />
                        </FormControl>
                        <FormDescription className="text-xs">
                          {dt(
                            'Use the rich text toolbar to format your questions with bold, italic, lists, and links. This content will be displayed with full formatting support.',
                          )}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </TabsContent>

              <TabsContent value="seo" className="space-y-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">
                    {dt('SEO & Social Media Open Graph Tags')}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {dt(
                      'These fields will be used for search engine optimization and social media sharing. If left empty, the system will use fallback values from the basic info.',
                    )}
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <FormField
                    control={form.control}
                    name="meta_title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Social Title</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="Custom SEO title (defaults to headline if empty)"
                            maxLength={60}
                          />
                        </FormControl>
                        <FormDescription>
                          Recommended: 50-60 characters. Will appear in search
                          results and browser tabs.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="meta_desc"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Social Description</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            placeholder="Custom SEO description (defaults to subheadline if empty)"
                            maxLength={160}
                            rows={3}
                          />
                        </FormControl>
                        <FormDescription>
                          Recommended: 150-160 characters. Will appear in search
                          results below the title.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="space-y-4">
                  <h4 className="text-base font-medium">
                    Twitter/X Specific Open Graph Tags
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Customize how your page appears when shared on Twitter/X.
                    Defaults to social title/description if empty.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <FormField
                    control={form.control}
                    name="x_title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Twitter/X Title</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="Custom Twitter/X title (defaults to social title if empty)"
                            maxLength={70}
                          />
                        </FormControl>
                        <FormDescription>
                          Recommended: 60-70 characters. Optimized for Twitter/X
                          cards.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="x_desc"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Twitter/X Description</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            placeholder="Custom Twitter/X description (defaults to social description if empty)"
                            maxLength={200}
                            rows={3}
                          />
                        </FormControl>
                        <FormDescription>
                          Recommended: 150-200 characters. Appears in Twitter/X
                          preview cards.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="x_img"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Twitter/X Image</FormLabel>
                        <FormControl>
                          <FileUpload
                            value={field.value}
                            onChange={field.onChange}
                            assetDir="starter-kits"
                            label="Upload Twitter/X Image"
                            accept={ALLOWED_UPLOAD_ASSETS_FORMATS}
                          />
                        </FormControl>
                        <FormDescription>
                          Recommended: 1200x630px. Will be used for Twitter/X
                          cards and Open Graph sharing.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="homepage_image"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{dt('Homepage Icon Image')} *</FormLabel>
                        <FormControl>
                          <FileUpload
                            value={field.value}
                            onChange={field.onChange}
                            assetDir="starter-kits"
                            label={dt('Upload Homepage Icon')}
                            accept={ALLOWED_UPLOAD_ASSETS_FORMATS}
                          />
                        </FormControl>
                        <FormDescription>
                          {dt(
                            'Icon image that will be displayed on the homepage template categories. Recommended: Square format (e.g., 200x200px).',
                          )}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </TabsContent>
            </Tabs>

            <div className="flex justify-end">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting
                  ? dt('Saving...')
                  : templateUseCaseData
                    ? dt('Update')
                    : dt('Create')}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
